import type { SaleFulfillmentDetail } from '../types/sales.types';

export type SaleFulfillmentCacheStatus = 'idle' | 'loading' | 'success' | 'error';

export interface SaleFulfillmentCacheEntry {
  detail: SaleFulfillmentDetail | null;
  error: string | null;
  status: SaleFulfillmentCacheStatus;
}

/**
 * Process-wide cache for mixed-sale fulfillment detail so the orders-table
 * expanded row and the order-details drawer share one snapshot (and one
 * in-flight request) instead of diverging local hook state.
 */
const entries = new Map<number, SaleFulfillmentCacheEntry>();
const inflight = new Map<number, Promise<SaleFulfillmentDetail>>();
type CacheListener = (orderId?: number) => void;
const listeners = new Set<CacheListener>();

function notify(orderId?: number): void {
  listeners.forEach((listener) => listener(orderId));
}

export function subscribeSaleFulfillmentDetail(listener: CacheListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSaleFulfillmentCacheEntry(
  orderId: number | null | undefined,
): SaleFulfillmentCacheEntry | null {
  if (orderId == null) return null;
  return entries.get(orderId) ?? null;
}

/** Test helper — clears shared state between unit tests. */
export function clearSaleFulfillmentDetailCache(): void {
  entries.clear();
  inflight.clear();
}

export function invalidateSaleFulfillmentDetail(orderId: number): void {
  entries.delete(orderId);
  notify(orderId);
}

/**
 * Force-refresh fulfillment detail for the given order IDs (list-refresh path).
 * Pass currently expanded row IDs so collapsed/stale cache entries are not fan-out refetched.
 * When `orderIds` is omitted, refreshes every cached id (legacy / tests).
 */
export async function refetchCachedSaleFulfillmentDetails(
  orderIds?: readonly number[],
): Promise<void> {
  const ids = orderIds != null ? orderIds.filter((id) => entries.has(id)) : [...entries.keys()];
  if (ids.length === 0) return;
  await Promise.all(
    ids.map(async (orderId) => {
      try {
        await fetchSaleFulfillmentDetail(orderId, { force: true });
      } catch {
        // Per-order errors stay on that cache entry; others still refresh.
      }
    }),
  );
}

export async function fetchSaleFulfillmentDetail(
  orderId: number,
  options: { force?: boolean } = {},
): Promise<SaleFulfillmentDetail> {
  const force = options.force === true;

  if (!force) {
    const cached = entries.get(orderId);
    if (cached?.status === 'success' && cached.detail != null) {
      return cached.detail;
    }

    const pending = inflight.get(orderId);
    if (pending) {
      return pending;
    }
  } else {
    const pending = inflight.get(orderId);
    if (pending) {
      try {
        await pending;
      } catch {
        // Continue with a forced refetch after the in-flight request settles.
      }
    }
  }

  const previous = entries.get(orderId);
  entries.set(orderId, {
    detail: previous?.detail ?? null,
    error: null,
    status: 'loading',
  });
  notify(orderId);

  const promise = (async () => {
    try {
      const { salesApi } = await import('./sales.service');
      const data = await salesApi.getSaleFulfillmentDetail(orderId);
      entries.set(orderId, {
        detail: data,
        error: null,
        status: 'success',
      });
      notify(orderId);
      return data;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'No se pudo cargar la información de logística.';
      entries.set(orderId, {
        detail: entries.get(orderId)?.detail ?? null,
        error: message,
        status: 'error',
      });
      notify(orderId);
      throw err;
    } finally {
      inflight.delete(orderId);
    }
  })();

  inflight.set(orderId, promise);
  return promise;
}
